// arrangePages — pure page/density math for tablet Arrange mode (#86bb5dx62).
//
// The working arrangement is the flat 64-slot `cameras` array (state.server.cameras);
// Arrange is a windowed editor over it — density = tiles per page, page = which
// window. These helpers derive page counts, auto-fit density on entry, per-page
// fill counts, and the array surgery for adding / deleting pages. Kept pure (no
// Redux, no React) so they unit-test cleanly.

import { ARRANGE_TILE_OPTIONS, ARRANGE_WIDE_TILE_OPTIONS, ARRANGE_MAX_DENSITY, ARRANGE_MAX_SLOTS } from './layouts';

// A slot holds a real camera when it is enabled. Empty placeholder slots are
// fEnable:false (matches selectView / assignCameraToSlot's empty shape).
const isFilled = (cam) => !!(cam && cam.fEnable);

// Index of the last filled slot, or -1 if the arrangement is empty.
export const lastFilledIndex = (cameras) => {
  if (!Array.isArray(cameras)) return -1;
  for (let i = cameras.length - 1; i >= 0; i--) {
    if (isFilled(cameras[i])) return i;
  }
  return -1;
};

// The span a view occupies = lastFilled + 1 (0 when empty). This — not the raw
// camera count — drives page math, so deliberate gaps between cameras are kept.
export const filledSpan = (cameras) => lastFilledIndex(cameras) + 1;

// Smallest offered density whose single page holds `span` cameras, but never above
// `cap` (the platform default — David: tablet caps at 8, desktop at 16). Fits DOWN for
// small views; when span exceeds the cap it stays at the cap and pages absorb the rest.
//   cap 8:  span 3 → 4 · span 6 → 8 · span 30 → 8    |    cap 16: span 19 → 16
export const fitDensity = (span, options = ARRANGE_TILE_OPTIONS, cap = ARRANGE_MAX_DENSITY) => {
  const usable = options.slice().sort((a, b) => a - b).filter((o) => o <= cap);
  const opts = usable.length ? usable : [Math.min(...options)];
  for (let i = 0; i < opts.length; i++) {
    if (opts[i] >= span) return opts[i];
  }
  return opts[opts.length - 1]; // span beyond the cap → the cap density, multi-page
};

// Auto-fit an existing view on Arrange entry: the platform default is the CEILING
// (tablet 8 / desktop 16) — never auto-jump past it — while small views still fit down.
// Then the page count reaches the last camera. Empty view → the default density, 1 page.
//   tablet (cap 8):   30-cam → 8, 4 pages · 6-cam → 8, 1 page · 4-cam → 4, 1 page
//   desktop (cap 16): 30-cam → 16, 2 pages · 19-cam → 16, 2 pages
export const autoFit = (cameras, defaultDensity, options = ARRANGE_TILE_OPTIONS) => {
  const span = filledSpan(cameras);
  if (span === 0) return { density: defaultDensity, pageCount: 1 };
  const density = fitDensity(span, options, defaultDensity); // defaultDensity = the cap
  return { density, pageCount: Math.max(1, Math.ceil(span / density)) };
};

// Page count needed to show every camera at a density (min 1). Used when the
// density changes — trailing empty pages fall away, intermediate ones (before the
// last camera) are preserved because they are inside the span.
export const pageCountFor = (cameras, density) =>
  Math.max(1, Math.ceil(filledSpan(cameras) / Math.max(1, density)));

// Filled-camera count for each page [0..pageCount), for the "Page N x/density" tabs.
export const pageFillCounts = (cameras, density, pageCount) => {
  const out = [];
  for (let p = 0; p < pageCount; p++) {
    const start = p * density;
    let n = 0;
    for (let i = start; i < start + density; i++) if (isFilled(cameras[i])) n++;
    out.push(n);
  }
  return out;
};

// Camera count on a single page (for the delete-page confirm copy).
export const pageFillCount = (cameras, density, page) => {
  let n = 0;
  const start = page * density;
  for (let i = start; i < start + density; i++) if (isFilled(cameras[i])) n++;
  return n;
};

// Can another page be added without exceeding the 64-slot working array?
export const canAddPage = (pageCount, density, maxSlots = ARRANGE_MAX_SLOTS) =>
  (pageCount + 1) * density <= maxSlots;

// Remove a page: splice out its density slots and shift later cameras UP into the
// gap, then pad the tail back to a full array with fresh empty placeholders. Later
// pages renumber automatically (their cameras keep their objects — and thus their
// compositeKey/stream identity — so React MOVES the tiles rather than remounting).
// `blank(i)` builds the empty placeholder for absolute slot i.
export const deletePageSlots = (cameras, page, density, blank, size = ARRANGE_MAX_SLOTS) => {
  const start = page * density;
  const kept = cameras.slice(0, start).concat(cameras.slice(start + density));
  const out = kept.slice(0, size);
  for (let i = out.length; i < size; i++) out.push(blank(i));
  return out;
};

// Density change: keep the flat arrangement, just re-window it. Returns the page
// count for the new density and a clamped current page.
export const rechunkForDensity = (cameras, density, currentPage) => {
  const pageCount = pageCountFor(cameras, density);
  return { pageCount, page: Math.min(currentPage, pageCount - 1) };
};

// Largest density available in EVERY tier — the base steps top out here (16); the wide steps (32/64)
// are landscape+desktop only. Derived from the data so it tracks ARRANGE_WIDE_TILE_OPTIONS.
export const NON_WIDE_MAX = Math.max(...ARRANGE_TILE_OPTIONS.filter((t) => !ARRANGE_WIDE_TILE_OPTIONS.includes(t)));

// When the viewport is NOT in the wide tier (tablet-portrait / mobile), a wide density (32/64) is
// unreachable via its now-hidden control and shouldn't keep rendering — clamp to the non-wide ceiling.
// Returns the density to switch to, or null when nothing needs to change. Pure → LiveView's matchMedia
// handler is a one-liner and the rule is unit-tested (#86bb7phhh).
export const clampWideDensity = (density, isWideTier) =>
  (!isWideTier && ARRANGE_WIDE_TILE_OPTIONS.includes(density)) ? NON_WIDE_MAX : null;

// Do two working arrangements hold the same cameras in the same slots? Compares
// enabled-ness + (bSerial,bID) per slot, ignoring the per-slot compositeKey — so it
// answers "is the canvas dirty vs the entry snapshot?" (and false after a revert).
export const arrangementsEqual = (a, b) => {
  if (a === b) return true;
  if (!a || !b || a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    const x = a[i], y = b[i];
    const xOn = !!(x && x.fEnable), yOn = !!(y && y.fEnable);
    if (xOn !== yOn) return false;
    if (xOn && (x.bID !== y.bID || (x.bSerial || 0) !== (y.bSerial || 0))) return false;
  }
  return true;
};

export { ARRANGE_MAX_DENSITY };
